import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PkgStatus, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { getPkgStatus, getPkgStatusTooltip, shippingMethodToLabel } from '@/helpers/pkgUtils'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/RoutePath'

import { BrandPreview } from '../../../../components/BrandPreview'
import { Divider } from '../../../../components/Divider'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover'
import ClientPkgDetails from './ClientPkgDetails'
import { DeliveryStatus } from './DeliveryStatus'
import { PkgReqMenu } from './PkgReqButtons'

// const getStatusColor = (pkg: UserPkg) => {
//   if (pkg.isShipped) return 'bg-green-100 text-green-800'
//   if (pkg.status === PkgStatus.NeedId) return 'bg-yellow-100 text-yellow-800'
//   return 'bg-button-primary-selected-segment text-text-secondary'
// }

interface Props {
  pkg: UserPkg
  currentView: 'all' | 'shipped' | 'identify'
  hideActions?: boolean
  hideStatus?: boolean
  isSelected?: boolean
  additionalStatus?: string
  onSelect?: (pkgId: string) => void
  onPkgPayment?: (pkg: UserPkg) => void
  onPkgIdentify?: (pkgId: string) => void
}
export const PkgPreview = ({
  pkg,
  currentView,
  hideActions,
  isSelected,
  additionalStatus,
  hideStatus,
  onSelect,
  onPkgPayment,
  onPkgIdentify,
}: Props) => {
  const isMobile = useIsMobile()
  const [isReqMenuOpen, setIsReqMenuOpen] = useState(false)
  const [isDeliveryStatusOpen, setIsDeliveryStatusOpen] = useState(false)

  const mainButton = useMemo(() => {
    // Show delivery status button for shipped packages (works in all views)
    if (pkg.isShipped) {
      return (
        <Button className='max-w-[126px]' onClick={() => setIsDeliveryStatusOpen(true)}>
          סטטוס משלוח
        </Button>
      )
    }

    // Don't show button if in non-all views
    if (currentView !== 'all') return null

    // Handle active requests (photo, shrink, unite)
    if (pkg.isPhotoRequestActive || pkg.isShrinkRequestActive || pkg.isUniteRequestActive) {
      return null
      // <AppTooltip triggerProps={{ asChild: true }} content='לא ניתן לשלח את החבילה כשיש בקשה בטיפול'>
      //   <Button disabled className='disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none'>
      //     בקשה ממתינה לביצוע
      //   </Button>
      // </AppTooltip>
    }
    if (pkg.isPaid && !pkg.isShipped) {
      return null
    }

    // Handle return requests
    if (pkg.isReturnRequestActive) {
      if (pkg.returnLabelFalse) {
        return (
          <Link to={`${RoutePath.ClientPkgReturn}/${pkg.id}`}>
            <Button className='max-w-[126px]'>העלה תווית החזרה</Button>
          </Link>
        )
      }

      return null
    }

    // Handle identification needed
    if (pkg.status === PkgStatus.NeedId && onPkgIdentify) {
      return (
        <Button className='max-w-[126px]' onClick={() => onPkgIdentify(pkg.id)}>
          המשך לזיהוי
        </Button>
      )
    }

    // Default: continue to shipping
    if (onPkgPayment) {
      return (
        <Button className='max-w-[126px]' onClick={() => onPkgPayment(pkg)}>
          המשך לשליחה
        </Button>
      )
    }

    return null
  }, [currentView, pkg, onPkgIdentify, onPkgPayment])
  const statusCmp = useMemo(() => {
    if (hideStatus) return null
    const tooltipContent = getPkgStatusTooltip(pkg)
    const status = getPkgStatus(pkg)
    if (!status) return null
    const content = (
      <div className='text-text-secondary bg-button-primary-selected-segment flex items-center gap-2 rounded-full px-3 py-1 text-center text-xs'>
        {status}
      </div>
    )
    if (tooltipContent) {
      return <AppTooltip content={tooltipContent}>{content}</AppTooltip>
    }
    return content
  }, [pkg, hideStatus])
  return (
    <div key={pkg.id} className='border-border-default flex flex-col gap-4 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm'>
      <div className='flex gap-6'>
        {onSelect && <Checkbox checked={isSelected} onCheckedChange={() => onSelect?.(pkg.id)} />}
        <div className='grow space-y-4'>
          {/* Package Header */}
          <div className='flex items-start justify-between'>
            <BrandPreview brand={pkg.brand} warehouse={pkg.warehouse} />
            <div className='flex flex-wrap items-center justify-end gap-3'>
              {additionalStatus && (
                <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-1 text-center text-xs'>
                  {additionalStatus}
                </div>
              )}
              {statusCmp}
            </div>
          </div>

          {/* Package Details */}
          <div className='flex flex-wrap items-center gap-8 text-sm'>
            <div>
              <p className='text-text-secondary text-xs'>תאריך הגעה</p>
              <p className='font-medium'>{pkg.arrivalDate.split('-').reverse().join('.')}</p>
            </div>
            <div className='hidden md:block'>
              <p className='text-text-secondary text-xs'>משקל</p>
              <p className='font-medium'>{pkg.dimensions.weight}KG</p>
            </div>
            <div>
              <p className='text-text-secondary text-xs'>כמות</p>
              <p className='font-medium'>{pkg.quantity} פריטים</p>
            </div>
            {pkg.valueInUSD > 0 && (
              <div>
                <p className='text-text-secondary text-xs'>שווי חבילה</p>
                <p className='font-medium'>{pkg.valueInUSD}$</p>
              </div>
            )}
            {pkg.isShipped && (
              <>
                <div>
                  <p className='text-text-secondary text-xs'>מספר מעקב</p>
                  <p className='font-medium'>{pkg.toTrackingNumber}</p>
                </div>
                <div>
                  <p className='text-text-secondary text-xs'>שיטת המשלוח</p>
                  <p className='font-medium'>{shippingMethodToLabel[pkg.shippingMethod]}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <Divider />

      {/* Actions */}
      <div>
        {pkg.status === 'needs_identification' && (
          <div className='space-y-3'>
            <div className='flex items-center justify-center gap-4'>
              <Button
                variant='outline'
                size='sm'
                // onClick={() => handlePackageIdentification(pkg.id, false)}
                // className={packageIdentifications[pkg.id] === false ? 'bg-button-primary-selected-segment' : ''}
              >
                לא
              </Button>
              <Button
                variant='outline'
                size='sm'
                // onClick={() => handlePackageIdentification(pkg.id, true)}
                // className={packageIdentifications[pkg.id] === true ? 'bg-button-primary-selected-segment' : ''}
              >
                כן
              </Button>
            </div>
            <p className='text-text-secondary text-center text-sm'>האם זו החבילה שלך?</p>
          </div>
        )}

        {isMobile ? (
          <div className='flex flex-col gap-2 transition-all duration-300 ease-in-out'>
            {!hideActions && <div className='flex w-full items-center justify-center'>{mainButton}</div>}
            <div className='flex items-center justify-between'>
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant='link' size='sm' className='text-text-link font-medium hover:no-underline'>
                    לפרטים נוספים
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <ClientPkgDetails pkg={pkg} />
                </DrawerContent>
              </Drawer>
              {!hideActions && !pkg.isShipped && !pkg.isPaid && (
                <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                  {isReqMenuOpen ? (
                    <ChevronUp className='text-text-secondary size-4' />
                  ) : (
                    <ChevronDown className='text-text-secondary size-4' />
                  )}
                  <span className='text-text-secondary text-xs'>פעולות נוספות</span>
                </Button>
              )}
            </div>
            <div
              className={cn('h-0 overflow-hidden overflow-x-auto opacity-0 transition-all duration-300 ease-in-out', {
                'h-12 opacity-100': isReqMenuOpen,
              })}
            >
              <PkgReqMenu pkg={pkg} className='justify-between' />
            </div>
          </div>
        ) : (
          <div className='flex items-center justify-between'>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant='link' size='sm' className='text-text-link font-medium hover:no-underline'>
                  לפרטים נוספים
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <ClientPkgDetails pkg={pkg} />
              </PopoverContent>
            </Popover>
            {!hideActions && (
              <>
                <div className='flex items-center gap-2'>
                  {!pkg.isShipped && !pkg.isPaid && (
                    <>
                      <div
                        className={cn(
                          'transform transition-all duration-300 ease-in-out',
                          isReqMenuOpen ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-2 opacity-0'
                        )}
                      >
                        <PkgReqMenu pkg={pkg} />
                      </div>
                      <Button variant='ghost' size='sm' className='gap-1' onClick={() => setIsReqMenuOpen(p => !p)}>
                        {isReqMenuOpen ? (
                          <ChevronLeft className='text-text-secondary size-4' />
                        ) : (
                          <ChevronRight className='text-text-secondary size-4' />
                        )}
                        <span className='text-text-secondary text-xs'>פעולות נוספות</span>
                      </Button>
                    </>
                  )}
                  {mainButton}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delivery Status Dialog */}
      <DeliveryStatus toTrackingNumber={pkg.toTrackingNumber} isOpen={isDeliveryStatusOpen} onOpenChange={setIsDeliveryStatusOpen} />
    </div>
  )
}
